import type { ReactionSummary } from "./reactions";

/** Emojis offered in the quick-react popover shown on a bubble. */
const QUICK_REACTIONS = ["👍", "❤️", "😂", "😮", "😢", "🙏"];

interface ReactionsProps {
  reactions: ReactionSummary[];
  onToggle: (key: string) => void;
  align: "left" | "right";
}

/** Aggregated reaction chips rendered under a message bubble (on chat bg). */
export function Reactions({ reactions, onToggle, align }: ReactionsProps) {
  if (reactions.length === 0) return null;
  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: 4,
        marginTop: 4,
        justifyContent: align === "right" ? "flex-end" : "flex-start",
      }}
    >
      {reactions.map((r) => (
        <button
          key={r.key}
          type="button"
          onClick={() => onToggle(r.key)}
          aria-pressed={r.mine}
          title={r.mine ? "Remove your reaction" : "React"}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 3,
            padding: "1px 8px",
            borderRadius: "var(--radius-pill)",
            fontSize: 12,
            cursor: "pointer",
            background: r.mine ? "var(--color-accent)" : "var(--color-panel)",
            color: r.mine ? "var(--color-accent-contrast)" : "var(--color-text)",
            border: `1px solid ${r.mine ? "var(--color-accent)" : "var(--color-border)"}`,
          }}
        >
          <span>{r.key}</span>
          <span style={{ fontWeight: 600 }}>{r.count}</span>
        </button>
      ))}
    </div>
  );
}

interface QuickReactRowProps {
  onPick: (emoji: string) => void;
  side: "left" | "right";
}

/** Small floating emoji row for quickly reacting to a message. */
export function QuickReactRow({ onPick, side }: QuickReactRowProps) {
  return (
    <div
      role="menu"
      style={{
        position: "absolute",
        bottom: 34,
        left: side === "left" ? 0 : "auto",
        right: side === "right" ? 0 : "auto",
        display: "flex",
        gap: 2,
        padding: 4,
        background: "var(--color-panel)",
        border: "1px solid var(--color-border)",
        borderRadius: "var(--radius-pill)",
        boxShadow: "var(--shadow-md)",
        zIndex: 5,
      }}
    >
      {QUICK_REACTIONS.map((e) => (
        <button
          key={e}
          type="button"
          onClick={() => onPick(e)}
          style={{
            border: "none",
            background: "transparent",
            cursor: "pointer",
            fontSize: 18,
            lineHeight: 1,
            padding: "2px 5px",
            borderRadius: "var(--radius-sm)",
          }}
        >
          {e}
        </button>
      ))}
    </div>
  );
}
